import { useEffect, useState } from "react";
import api, { INR, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, RefreshCw } from "lucide-react";

const SUB_DEALERS = ["Alok Automobile", "Dilip Enterprises"];

export default function Incentives() {
  const [rules, setRules] = useState([]);
  const [modelHints, setModelHints] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const fetchAll = async () => {
    const { data } = await api.get("/incentives");
    setRules(data);
  };
  const fetchHints = async () => {
    try {
      const { data } = await api.get("/models");
      setModelHints(data.models || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { fetchAll(); fetchHints(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`Are you sure you want to delete the incentive rule for ${r.sub_dealer} · ${r.vehicle_model}?`)) return;
    try { await api.delete(`/incentives/${r.id}`); toast.success("Rule deleted"); fetchAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const syncLedger = async () => {
    setSyncing(true);
    try {
      await api.post("/ledgers/backfill");
      toast.success("Ledger sync queued — running in background");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="incentives-heading">Incentive Master</h1>
          <p className="text-sm text-slate-500 mt-1">Per-model fixed incentive rules for sub-dealers (Alok Automobile &amp; Dilip Enterprises). Each model has its own rule. Applied automatically to every delivered vehicle — past, present and future.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={syncLedger} disabled={syncing} data-testid="sync-ledger-button"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm disabled:opacity-60">
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Queuing…" : "Sync Ledger"}
          </button>
          <button data-testid="new-incentive-button" onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-orange bg-brand-orange-hover text-white text-sm">
            <Plus className="w-4 h-4" /> New Rule
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg table-scroll">
        <table className="data-table w-full" data-testid="incentives-table">
          <thead>
            <tr>
              <th>Sub-Dealer</th>
              <th>Vehicle Model</th>
              <th className="text-right">Fixed Incentive</th>
              <th>Active</th>
              <th>Notes</th>
              <th className="text-right pr-6">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && (
              <tr><td colSpan="6" className="text-center text-slate-400 py-8">No incentive rules yet.</td></tr>
            )}
            {rules.map(r => (
              <tr key={r.id} data-testid={`incentive-row-${r.id}`}>
                <td className="font-medium">{r.sub_dealer}</td>
                <td>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${r.vehicle_model === "ALL" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-800"}`}>
                    {r.vehicle_model}
                  </span>
                </td>
                <td className="text-right font-semibold text-brand-orange">{INR(r.fixed_amount)}</td>
                <td>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${r.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
                    {r.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="text-xs text-slate-500 max-w-xs truncate">{r.notes || "—"}</td>
                <td className="text-right pr-6">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setEditing(r)} data-testid={`edit-incentive-${r.id}`}
                      className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(r)} data-testid={`delete-incentive-${r.id}`}
                      className="p-1.5 rounded-md hover:bg-red-50 text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(showForm || editing) && (
        <IncentiveForm initial={editing} modelHints={modelHints}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchAll(); }} />
      )}
    </div>
  );
}

function IncentiveForm({ initial, modelHints, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial ? {
    sub_dealer: initial.sub_dealer, vehicle_model: initial.vehicle_model,
    fixed_amount: initial.fixed_amount, active: initial.active, notes: initial.notes || "",
  } : {
    sub_dealer: SUB_DEALERS[0], vehicle_model: "",
    fixed_amount: 0, active: true, notes: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_model || !form.vehicle_model.trim()) {
      toast.error("Vehicle Model is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, fixed_amount: Number(form.fixed_amount) || 0, vehicle_model: form.vehicle_model.trim() };
      if (isEdit) {
        await api.patch(`/incentives/${initial.id}`, payload);
        toast.success("Rule updated");
      } else {
        await api.post("/incentives", payload);
        toast.success("Rule created");
      }
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="incentive-form-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="font-heading text-xl font-semibold mb-4">{isEdit ? "Edit Rule" : "New Incentive Rule"}</div>
        <div className="space-y-3">
          <Field label="Sub-Dealer">
            <select value={form.sub_dealer} onChange={(e) => setForm({ ...form, sub_dealer: e.target.value })}
              className="input" data-testid="if-sub_dealer">
              {SUB_DEALERS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Vehicle Model (type freely — e.g., Elite+, Cargo, Rik Twin, ALL)">
            <input list="incentive-model-hints" value={form.vehicle_model}
              onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })}
              className="input" data-testid="if-vehicle_model" placeholder="Model name" required />
            <datalist id="incentive-model-hints">
              <option value="ALL" />
              {modelHints.map(m => <option key={m} value={m} />)}
            </datalist>
          </Field>
          <Field label="Fixed Incentive (₹)">
            <input type="number" min="0" step="1" value={form.fixed_amount}
              onChange={(e) => setForm({ ...form, fixed_amount: e.target.value })} className="input" data-testid="if-fixed_amount" required />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="if-active" />
            Rule is active
          </label>
          <Field label="Notes">
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" data-testid="if-notes" />
          </Field>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm border border-slate-300" data-testid="if-cancel">Cancel</button>
          <button type="submit" disabled={saving}
            className="px-4 py-2 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white disabled:opacity-60" data-testid="if-submit">
            {saving ? "Saving…" : (isEdit ? "Update" : "Create")}
          </button>
        </div>
        <style>{`.input { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #CBD5E1; border-radius: 0.375rem; font-size: 0.875rem; outline: none; background: white; }`}</style>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{label}</div>
    {children}
  </label>
);
